// Libraries
import React from 'react';
import get from 'lodash/get';

// Components
import { CardBlock, CardText } from 'Components/flowChart/components/NodePreview/components/styled';

// Types
import {
	FILTER_TYPE_LABEL,
	SEGMENT_TYPE_LABEL,
} from 'Components/flowChart/components/NodePreview/components/NodeYes/constants';
import { IFilterBlast, IMetadata } from 'Components/flowChart/components/NodePreview/components/NodeYes/types';
import { INodePreviewProps } from 'Components/flowChart/components/NodePreview/types';

// Utils
import { getFilterInfoBlast } from 'Components/flowChart/components/NodePreview/components/NodeYes/utils';
import { countFilterSegments, countFilterUniqueAttrs } from 'Components/flowChart/utils';

const PATH = 'components/components/flowChart/components/Card/components/NodeYes/index.tsx';

export interface INodeYes extends INodePreviewProps<IMetadata> {}

const NodeYes: React.FC<INodeYes> = (props) => {
	try {
		const {
			node: { metadata },
		} = props;
		const isBlast: boolean = !!get(metadata, 'filters.includedFilters', false);

		if (isBlast) {
			const { includedFilters, excludedFilters } = metadata.filters as IFilterBlast;

			const {
				segmentType, //
				userType,
				numIncludedSegments,
				numExcludedSegments,
				numIncludedUsers,
				numExcludedUsers,
			} = getFilterInfoBlast({ includedFilters, excludedFilters });

			if ([numIncludedSegments, numIncludedUsers, numExcludedSegments, numExcludedUsers].every((item) => !item)) {
				return null;
			}

			const audienceTypeSegment = SEGMENT_TYPE_LABEL[segmentType!];
			const specificTypeSegment = SEGMENT_TYPE_LABEL[userType!];

			return (
				<CardBlock>
					{numIncludedSegments > 0 && (
						<CardText>{`Included ${numIncludedSegments} ${audienceTypeSegment} segment(s)`}</CardText>
					)}
					{numIncludedUsers > 0 && (
						<CardText>{`Included ${numIncludedUsers} specific ${specificTypeSegment}(s)`}</CardText>
					)}
					{numExcludedSegments > 0 && (
						<CardText>{`Excluded ${numExcludedSegments} ${audienceTypeSegment} segment(s)`}</CardText>
					)}
					{numExcludedUsers > 0 && (
						<CardText>{`Excluded ${numExcludedUsers} specific ${specificTypeSegment}(s)`}</CardText>
					)}
				</CardBlock>
			);
		} else {
			const { filterType } = metadata;

			if (filterType === 'user_attribute' || filterType === 'customer_attribute') {
				const { filterType, filters } = metadata;
				const numAttributes = countFilterUniqueAttrs(filters);

				return (
					<>
						<CardBlock>
							<CardText>Filter by audience attribute</CardText>
						</CardBlock>
						<CardBlock>
							<CardText>Condition:</CardText>
							<CardText>{`Target to ${FILTER_TYPE_LABEL[filterType]}`}</CardText>
							<CardText>{`Have ${numAttributes} attribute(s)`}</CardText>
						</CardBlock>
					</>
				);
			}

			if (filterType === 'item_segment') {
				const { audienceSegmentType, excludedFilters, filters } = metadata;

				const numIncluded = countFilterSegments(filters);
				const numExcluded = countFilterSegments(excludedFilters!);

				return (
					<>
						<CardBlock>
							<CardText>Filter by audience segment</CardText>
						</CardBlock>
						<CardBlock>
							<CardText>Condition:</CardText>
							{numIncluded > 0 && (
								<CardText>{`Included ${numIncluded} ${SEGMENT_TYPE_LABEL[audienceSegmentType!]} segment(s)`}</CardText>
							)}
							{numExcluded > 0 && (
								<CardText>{`Excluded ${numExcluded} ${SEGMENT_TYPE_LABEL[audienceSegmentType!]} segment(s)`}</CardText>
							)}
						</CardBlock>
					</>
				);
			}

			if (filterType === 'event_attribute') {
				const { filters } = metadata;

				const numAttributes = countFilterUniqueAttrs(filters);

				return (
					<>
						<CardBlock>
							<CardText>Filter by trigger event attribute</CardText>
						</CardBlock>
						<CardBlock>
							<CardText>Condition:</CardText>
							<CardText>{`Have ${numAttributes} attributes(s)`}</CardText>
						</CardBlock>
					</>
				);
			}
		}

		return null;
	} catch (e) {
		props.onError?.(e, {
			component: PATH,
			action: 'render -> NodeYes',
			args: { props },
		});

		return null;
	}
};

export default NodeYes;
